import { Group, Stack } from '@mantine/core';
import * as React from 'react';
import { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { SidebarItem, useContextSidebarType, useSidebarItems, useSidebarState } from './hooks/useSidebar';

interface ContextSidebarProps {
  items: SidebarItem[];
  sidebarHeader?: ReactNode;
  sidebarFooter?: ReactNode;
  navbarFooter?: ReactNode;

  baseUrl: string;
}

interface LinkBarProps {
  links: ReactNode;
}

function Sidebar({ links }: LinkBarProps) {
  return <Stack pt="3vh">{links}</Stack>;
}

function Navbar({ links }: LinkBarProps) {
  return (
    <Group
      pb="xs"
      wrap="nowrap"
      bg="var(--mantine-color-sidebar-context-filled)"
    >
      {links}
    </Group>
  );
}

export function ContextSidebar({ items, sidebarHeader, sidebarFooter, navbarFooter, baseUrl }: ContextSidebarProps) {
  const [domReady, setDomReady] = React.useState(false);
  const { isMainActive, setMainActive, setHasSidebarContext } = useSidebarState();
  const type = useContextSidebarType();
  const links = useSidebarItems({
    baseUrl,
    items,
    header: type === 'sidebar' ? sidebarHeader : null,
    footer: type === 'sidebar' ? sidebarFooter : null,
    isCollapsed: type === 'sidebar' ? isMainActive : false,
  });

  React.useEffect(() => {
    setDomReady(true);
    setMainActive(type != 'sidebar');

    return () => {
      setMainActive(true);
      setHasSidebarContext(false);
    };
  }, [type, setMainActive, setHasSidebarContext]);

  return domReady ? (
    <>
      {createPortal(
        type == 'sidebar' ? <Sidebar links={links} /> : <Navbar links={links} />,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        document.getElementById(type == 'sidebar' ? 'context-sidebar' : 'context-navbar')!,
      )}
      {type === 'navbar' &&
        navbarFooter !== null &&
        createPortal(
          <>{navbarFooter}</>,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          document.getElementById('context-footer')!,
        )}
    </>
  ) : null;
}
